import React, { useState } from 'react';
import { Box, Typography, Paper, Chip, Avatar, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, LinearProgress, useMediaQuery } from '@mui/material';
import type { ChipColor, ProgressColor } from '@/types/chip';
import { useNavigate } from 'react-router-dom';
import { PersonAdd } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import { WizardListToolbar } from '@/components/workflow/WizardListToolbar';
import { useEmployeeMap, getEmployeeName, getEmployeeField } from '@/hooks/useEmployeeMap';
import { useGetList } from 'react-admin';
import type { OnboardingRecord, OnboardingStep } from './constants';
import { STATUS_COLORS } from './constants';

export const OnboardingProcessList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: processes, isLoading } = useGetList('onboarding-processes', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: { breakpoints: { down: (key: string) => string } }) => theme.breakpoints.down('md'));
  const employeeMap = useEmployeeMap();

  const stats = {
    total: processes?.length ?? 0,
    active: processes?.filter((p: OnboardingRecord) => p.status === 'InProgress').length ?? 0,
    completed: processes?.filter((p: OnboardingRecord) => p.status === 'Completed').length ?? 0,
    cancelled: processes?.filter((p: OnboardingRecord) => p.status === 'Cancelled').length ?? 0,
    overdue: processes?.filter((p: OnboardingRecord) => p.targetStartDate && new Date(p.targetStartDate) < new Date() && p.status !== 'Completed').length ?? 0,
  };

  const stepData = processes?.reduce((acc: Record<string, number>, p: OnboardingRecord) => {
    const steps = p.steps?.length ? p.steps : [];
    const completed = steps.filter((s: OnboardingStep) => s.status === 'Completed').length;
    if (completed === steps.length && steps.length > 0) acc['Complete'] = (acc['Complete'] || 0) + 1;
    else if (completed >= 4) acc['Provisioning'] = (acc['Provisioning'] || 0) + 1;
    else if (completed >= 3) acc['POPIA'] = (acc['POPIA'] || 0) + 1;
    else if (completed >= 2) acc['Contract'] = (acc['Contract'] || 0) + 1;
    else if (completed >= 1) acc['Documents'] = (acc['Documents'] || 0) + 1;
    else acc['Initiation'] = (acc['Initiation'] || 0) + 1;
    return acc;
  }, {});

  const pieData: { label: string; value: number }[] = Object.entries(stepData ?? {}).map(([label, value]) => ({ label, value: value as number }));

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <WizardListToolbar domainId="hr.onboarding" wizardPath="wizard/onboarding" label="Onboarding Wizard" />
        </Box>
        <ListPageHeader
          title="Onboarding Processes"
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'info.main' },
            { label: 'Completed', value: String(stats.completed), color: 'success.main' },
            { label: 'Overdue', value: String(stats.overdue), color: 'error.main' },
            { label: 'Cancelled', value: String(stats.cancelled), color: 'default' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {processes?.map((item: OnboardingRecord) => {
            const steps = item.steps?.length ? item.steps : [];
            const completedSteps = steps.filter((s: OnboardingStep) => s.status === 'Completed').length;
            const isOverdue = item.targetStartDate && new Date(item.targetStartDate) < new Date() && item.status !== 'Completed';
            return (
              <Paper key={item.id} sx={{ p: 2 }} onClick={() => navigate(`/onboarding-processes/${item.id}/show`)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.main' }}>
                      {getEmployeeName(employeeMap, item.employee?.id).charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>{getEmployeeName(employeeMap, item.employee?.id)}</Typography>
                    </Box>
                  </Box>
                  <Chip label={item.status} size="small" color={STATUS_COLORS[item.status ?? ''] as ChipColor} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">{getEmployeeField(employeeMap, item.employee?.id, 'department')}</Typography>
                  <Typography variant="caption" color={isOverdue ? 'error.main' : 'text.secondary'}>
                    Start: {item.targetStartDate}
                  </Typography>
                  <Typography variant="caption" sx={{ ml: 'auto', fontWeight: 600 }}>
                    {completedSteps}/{steps.length} steps
                  </Typography>
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <WizardListToolbar domainId="hr.onboarding" wizardPath="wizard/onboarding" label="Onboarding Wizard" />
      </Box>
      <ListPageHeader
        title="Onboarding Processes"
        metrics={[
          { label: 'Active', value: String(stats.active), color: 'info.main' },
          { label: 'Completed', value: String(stats.completed), color: 'success.main' },
          { label: 'Overdue', value: String(stats.overdue), color: 'error.main' },
          { label: 'Cancelled', value: String(stats.cancelled), color: 'default' },
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
        ]}
        pieChartData={pieData}
        pieChartTitle="Pipeline by Stage"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Steps</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Progress</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Target Start</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {processes?.map((item: OnboardingRecord) => {
              const steps = item.steps?.length ? item.steps : [];
              const completedSteps = steps.filter((s: OnboardingStep) => s.status === 'Completed').length;
              const progress = steps.length > 0 ? Math.round((completedSteps / steps.length) * 100) : 0;
              const isOverdue = item.targetStartDate && new Date(item.targetStartDate) < new Date() && item.status !== 'Completed';
              return (
                <TableRow
                  key={item.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/onboarding-processes/${item.id}/show`)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.main' }}>
                        {getEmployeeName(employeeMap, item.employee?.id).charAt(0)}
                      </Avatar>
                      <Typography variant="body2">{getEmployeeName(employeeMap, item.employee?.id)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{getEmployeeField(employeeMap, item.employee?.id, 'department')}</Typography>
                  </TableCell>
                  <TableCell>
                  <Chip label={item.status} size="small" color={STATUS_COLORS[item.status ?? '']} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{completedSteps}/{steps.length}</Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ flex: 1, height: 6, borderRadius: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary">{progress}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={isOverdue ? 'error.main' : 'text.primary'}>
                      {item.targetStartDate || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={processes?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!processes || processes.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <PersonAdd sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No onboarding processes found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
